import { useEffect, useState } from "react";
import { Lock, LogOut, RefreshCw, Inbox, CalendarClock, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { LogoMark } from "@/components/site/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sysy_admin_token";

const STATUS_STYLES = {
  new: "bg-sunburst text-ink",
  contacted: "bg-tang text-white",
  booked: "bg-[#4CAF50] text-white",
  declined: "bg-ink/20 text-ink/60",
};

const TYPE_LABELS = { vendor: "Vendor Event", school: "School Event", birthday: "Birthday Party" };

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Wrong password");
      sessionStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      toast.error(err.message || "Wrong password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-login-card" className="flex min-h-screen items-center justify-center bg-sunburst dots-bg px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-3xl border-4 border-ink bg-white p-8 shadow-popXl"
      >
        <div className="flex justify-center">
          <LogoMark className="h-14 w-14" />
        </div>
        <h1 className="font-display mt-4 text-center text-2xl font-bold">Bookings Dashboard</h1>
        <p className="mt-1 text-center text-sm font-semibold text-ink/60">Sy Sy's Snacks. Private area</p>
        <label className="mt-6 block text-xs font-black uppercase tracking-[0.14em] text-ink/70" htmlFor="admin-pass">
          Password
        </label>
        <input
          id="admin-pass"
          data-testid="admin-password-input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-1.5 w-full rounded-xl border-[3px] border-ink bg-cream px-4 py-3 text-base font-semibold focus:outline-none focus:ring-4 focus:ring-sunburst/60"
          placeholder="••••••••"
          autoFocus
        />
        <button
          type="submit"
          data-testid="admin-login-button"
          disabled={busy}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-full border-[3px] border-ink bg-tang px-6 py-3 text-sm font-black uppercase tracking-wide text-white shadow-pop transition-all duration-200 hover:-translate-y-0.5 hover:bg-tang-dark disabled:opacity-60"
        >
          <Lock className="h-4 w-4" strokeWidth={3} /> {busy ? "Checking…" : "Unlock Dashboard"}
        </button>
      </form>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/bookings`, { headers: { "X-Admin-Token": token } });
      if (res.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        onLogout();
        return;
      }
      const data = await res.json();
      setBookings(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Couldn't load bookings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setStatus = async (id, status) => {
    try {
      const res = await fetch(`${API}/bookings/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-Admin-Token": token },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error();
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Couldn't update status");
    }
  };

  const count = (s) => bookings.filter((b) => b.status === s).length;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-cream">
      <header className="sticky top-0 z-40 border-b-[3px] border-ink bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <LogoMark className="h-9 w-9" />
            <span className="font-display text-lg font-bold">Sy Sy's Snacks Bookings</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="admin-refresh-button"
              onClick={load}
              className="inline-flex items-center gap-2 rounded-full border-[3px] border-ink bg-sunburst px-4 py-2 text-xs font-black uppercase tracking-wide shadow-popSm transition-all duration-200 hover:-translate-y-0.5"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} strokeWidth={3} /> Refresh
            </button>
            <a
              href="/"
              className="hidden rounded-full border-[3px] border-ink bg-white px-4 py-2 text-xs font-black uppercase tracking-wide shadow-popSm transition-all duration-200 hover:-translate-y-0.5 sm:inline-flex"
            >
              View Site
            </a>
            <button
              type="button"
              data-testid="admin-logout-button"
              onClick={() => {
                sessionStorage.removeItem(TOKEN_KEY);
                onLogout();
              }}
              className="inline-flex items-center gap-2 rounded-full border-[3px] border-ink bg-tomato px-4 py-2 text-xs font-black uppercase tracking-wide text-white shadow-popSm transition-all duration-200 hover:-translate-y-0.5"
            >
              <LogOut className="h-4 w-4" strokeWidth={3} /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <div className="grid gap-5 sm:grid-cols-3">
          {[
            { label: "Total Requests", value: bookings.length, icon: Inbox, cls: "bg-white" },
            { label: "New", value: count("new"), icon: CalendarClock, cls: "bg-sunburst" },
            { label: "Booked", value: count("booked"), icon: CalendarCheck, cls: "bg-tang text-white" },
          ].map((s) => (
            <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`} className={`rounded-2xl border-[3px] border-ink p-5 shadow-pop ${s.cls}`}>
              <s.icon className="h-6 w-6" strokeWidth={2.75} />
              <p className="font-display mt-2 text-4xl font-bold">{s.value}</p>
              <p className="text-xs font-black uppercase tracking-widest opacity-70">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 overflow-x-auto rounded-2xl border-4 border-ink bg-white shadow-pop">
          <table data-testid="bookings-table" className="w-full min-w-[900px] text-left text-sm">
            <thead>
              <tr className="border-b-[3px] border-ink bg-cream text-xs font-black uppercase tracking-wider text-ink/60">
                <th className="px-4 py-3">Requested</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Event Date</th>
                <th className="px-4 py-3">Guests</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Location</th>
                <th className="px-4 py-3">Message</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {bookings.length === 0 && !loading && (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center font-bold text-ink/50">
                    No booking requests yet. They'll show up here the moment someone books.
                  </td>
                </tr>
              )}
              {bookings.map((b) => (
                <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-b-2 border-ink/10 align-top font-semibold">
                  <td className="whitespace-nowrap px-4 py-3 text-ink/60">{formatDate(b.created_at)}</td>
                  <td className="px-4 py-3 font-black">{b.name}</td>
                  <td className="px-4 py-3">{TYPE_LABELS[b.event_type] || b.event_type}</td>
                  <td className="whitespace-nowrap px-4 py-3">{formatDate(b.event_date)}</td>
                  <td className="px-4 py-3">{b.guests || "—"}</td>
                  <td className="px-4 py-3">
                    <a href={`mailto:${b.email}`} className="text-tang underline underline-offset-2">{b.email}</a>
                    {b.phone && <span className="block text-ink/60">{b.phone}</span>}
                  </td>
                  <td className="px-4 py-3 text-ink/70">{b.location || "—"}</td>
                  <td className="max-w-[220px] px-4 py-3 text-ink/70">{b.message || "—"}</td>
                  <td className="px-4 py-3">
                    <select
                      data-testid={`status-select-${b.id}`}
                      value={b.status}
                      onChange={(e) => setStatus(b.id, e.target.value)}
                      className={`cursor-pointer rounded-full border-2 border-ink px-3 py-1.5 text-xs font-black uppercase tracking-wide ${STATUS_STYLES[b.status] || "bg-white"}`}
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="booked">Booked</option>
                      <option value="declined">Declined</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));

  if (!token) return <AdminLogin onLogin={setToken} />;
  return <Dashboard token={token} onLogout={() => setToken(null)} />;
}
